import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $createTextNode,
  $getSelection,
  $setSelection,
  $createRangeSelection,
  $getNodeByKey,
  $isRangeSelection,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
  COMMAND_PRIORITY_LOW,
  CLICK_COMMAND
} from 'lexical';
import { mergeRegister } from '@lexical/utils';

import { MatchingEntityMentionNode } from '../../molecules/MatchingEntityMentionNode';
import { setEntitySelectCallback } from '../../molecules/MatchingEntityMentionNode/MatchingEntityMentionComponent';

import { Trie } from '../../../utils';
import {
  getTextOffsetInParagraph,
  findPositionInParagraph,
  ensureTextNodesAroundSpecialNodes,
  areSpecialNodesLogicallyAdjacent
} from '../../../utils/lexicalUtils';
import { sourceShape } from '../../../propsShape';

function transformTextNodesToEntityMentions(
  editor,
  entityMatches,
  sources = [],
  restoreCursor = true,
  textOffset = null,
  paragraphKey = null,
  terms = []
) {
  // Check if text contains terms that should take priority over entity creation
  const textMatchesAnyTerm = (text) => {
    if (!terms || terms.length === 0) return false;

    const trimmedText = text.trim().toLowerCase();

    return terms.some(({ termList }) =>
      termList.some((term) => {
        const termLower = term.toLowerCase();
        const regex = new RegExp(`\\b${termLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        return regex.test(trimmedText);
      })
    );
  };

  editor.update(
    () => {
      // Save cursor position for restoration after transformation
      let savedTextOffset = textOffset;
      let savedParagraphKey = paragraphKey;

      if (restoreCursor && (savedTextOffset === null || savedParagraphKey === null)) {
        const selection = $getSelection();

        if (selection && $isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          let paragraph = anchorNode;

          // Find the paragraph containing the cursor
          while (paragraph && paragraph.getType() !== 'paragraph') {
            paragraph = paragraph.getParent();
          }

          if (paragraph) {
            savedParagraphKey = paragraph.getKey();
            // Calculate text offset from start of paragraph
            savedTextOffset = getTextOffsetInParagraph(
              paragraph,
              anchorNode,
              selection.anchor.offset
            );
          }
        }
      }

      const root = $getRoot();
      const paragraphNodes = root.getChildren().filter((node) => node.getType() === 'paragraph');

      paragraphNodes.forEach((paragraphNode) => {
        let previousTextNode = null;
        let newChildren = [];
        const children = paragraphNode.getChildren();

        // PASS 1: Convert adjacent special nodes to text for re-evaluation
        children.forEach((node, index) => {
          const nodeType = node.getType();
          const nodeText = node.getTextContent();

          if (nodeType === 'entity') {
            // Update existing entity nodes with new suggestions if available
            const matchingEntity = entityMatches?.find(
              ({ entity }) => entity.toLowerCase() === nodeText.toLowerCase()
            );

            if (matchingEntity && matchingEntity.suggestions) {
              if (node.setSuggestions) {
                node.setSuggestions(matchingEntity.suggestions);
              }
            }

            // Check if entity could be part of a longer entity match
            const couldBePartOfLongerEntity =
              entityMatches &&
              entityMatches.some(({ entity }) => {
                const entityLower = entity.toLowerCase();
                const nodeTextLower = nodeText.toLowerCase();

                // Check if current entity is prefix of longer available entity
                const isPrefix =
                  entityLower.startsWith(`${nodeTextLower} `) ||
                  entityLower.startsWith(nodeTextLower);

                if (isPrefix) {
                  // Check if adjacent text could complete the longer entity
                  const nextSibling = index < children.length - 1 ? children[index + 1] : null;
                  const prevSibling = index > 0 ? children[index - 1] : null;

                  if (nextSibling && nextSibling.getType() === 'text') {
                    const combinedText = nodeText + nextSibling.getTextContent();
                    const combinedLower = combinedText.toLowerCase().trim();
                    const matchesLongerEntity =
                      entityLower === combinedLower || entityLower.startsWith(combinedLower);
                    return matchesLongerEntity;
                  }

                  if (prevSibling && prevSibling.getType() === 'text') {
                    const combinedText = prevSibling.getTextContent() + nodeText;
                    const combinedLower = combinedText.toLowerCase().trim();
                    const matchesLongerEntity =
                      entityLower === combinedLower || entityLower.endsWith(combinedLower);
                    return matchesLongerEntity;
                  }
                }

                return false;
              });

            // Check for adjacency to other special nodes
            const hasAdjacentTermNode = areSpecialNodesLogicallyAdjacent(children, index, 'term');
            const hasAdjacentEntityNode = areSpecialNodesLogicallyAdjacent(
              children,
              index,
              'entity'
            );

            // Check for direct text adjacency (user deleted space)
            const isDirectlyAdjacentToText = () => {
              const prevSibling = index > 0 ? children[index - 1] : null;
              const nextSibling = index < children.length - 1 ? children[index + 1] : null;

              // Detect text nodes without separating whitespace
              const adjacentToPrevText =
                prevSibling &&
                prevSibling.getType() === 'text' &&
                prevSibling.getTextContent().length > 0 &&
                !/\s$/.test(prevSibling.getTextContent());

              const adjacentToNextText =
                nextSibling &&
                nextSibling.getType() === 'text' &&
                nextSibling.getTextContent().length > 0 &&
                !/^\s/.test(nextSibling.getTextContent());

              return adjacentToPrevText || adjacentToNextText;
            };

            const isAdjacentToText = isDirectlyAdjacentToText();

            // Prioritize terms over entities if text matches term patterns
            if (textMatchesAnyTerm(nodeText)) {
              newChildren.push(node);
              previousTextNode = null;
            } else if (
              hasAdjacentTermNode ||
              couldBePartOfLongerEntity ||
              hasAdjacentEntityNode ||
              isAdjacentToText
            ) {
              // Convert entity to text for re-evaluation when adjacent conflicts exist
              const textNode = $createTextNode(nodeText);
              if (previousTextNode) {
                const prevText = previousTextNode.getTextContent();
                const newText = textNode.getTextContent();
                const mergedText = prevText + newText; // Respect user's spacing choice

                previousTextNode.setTextContent(mergedText);
              } else {
                newChildren.push(textNode);
                previousTextNode = textNode;
              }
            } else {
              // Keep standalone entity node (with updated suggestions)
              newChildren.push(node);
              previousTextNode = null;
            }
          } else if (nodeType === 'term') {
            // Convert terms adjacent to entities to text for re-evaluation
            const hasAdjacentEntityNode = areSpecialNodesLogicallyAdjacent(
              children,
              index,
              'entity'
            );

            if (hasAdjacentEntityNode) {
              const textNode = $createTextNode(nodeText);
              if (previousTextNode) {
                const prevText = previousTextNode.getTextContent();
                const newText = textNode.getTextContent();
                const mergedText = prevText + newText;

                previousTextNode.setTextContent(mergedText);
              } else {
                newChildren.push(textNode);
                previousTextNode = textNode;
              }
            } else {
              // Keep standalone term node
              newChildren.push(node);
              previousTextNode = null;
            }
          } else if (nodeType === 'text') {
            // Merge consecutive text nodes
            if (previousTextNode) {
              const prevText = previousTextNode.getTextContent();
              const newText = nodeText;
              const mergedText = prevText + newText;
              previousTextNode.setTextContent(mergedText);
            } else {
              newChildren.push(node);
              previousTextNode = node;
            }
          } else {
            // Keep other node types as-is
            newChildren.push(node);
            previousTextNode = null;
          }
        });

        // PASS 2: Transform text nodes to entity nodes where matches found
        newChildren = newChildren.flatMap((node) => {
          if (node.getType() !== 'text') {
            return node;
          }

          const nodeText = node.getTextContent();

          // Skip entity creation if text should be terms instead
          if (textMatchesAnyTerm(nodeText)) {
            return node;
          }

          const exactMatches = [];
          if (entityMatches && entityMatches.length > 0) {
            // Sort entities by length to prioritize longer matches
            const sortedEntityMatches = [...entityMatches].sort(
              (a, b) => b.entity.length - a.entity.length
            );

            sortedEntityMatches.forEach((match) => {
              const { entity, suggestions } = match;
              // Use word boundaries for exact matching
              const escapedEntity = entity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const regex = new RegExp(`\\b${escapedEntity}\\b`, 'gi');
              const matches = nodeText.match(regex);

              if (matches && matches.length > 0) {
                matches.forEach((matchText) => {
                  // Prevent overlapping matches
                  const wouldOverlap = exactMatches.some((existing) => {
                    const nodeTextLower = nodeText.toLowerCase();
                    const existingStart = nodeTextLower.indexOf(existing.match.toLowerCase());
                    const existingEnd = existingStart + existing.match.length;
                    const newStart = nodeTextLower.indexOf(matchText.toLowerCase());
                    const newEnd = newStart + matchText.length;

                    return newStart < existingEnd && newEnd > existingStart;
                  });

                  if (!wouldOverlap) {
                    exactMatches.push({ entity, match: matchText, suggestions });
                  }
                });
              }
            });
          }

          if (exactMatches.length === 0) {
            return node;
          }

          // Use Trie for efficient text processing
          const trie = new Trie();
          exactMatches
            .sort((a, b) => b.match.length - a.match.length)
            .forEach(({ match }) => {
              trie.insert(match);
            });

          let result = '';
          let i = 0;
          const text = nodeText;
          const newNodes = [];

          // Process text character by character, creating entity nodes for matches
          while (i < text.length) {
            const matchEnd = trie.searchInText(text, i);

            if (matchEnd !== -1) {
              const matchText = text.slice(i, matchEnd);

              // Skip mentions and special character prefixes
              const startsWithSpecialChar = matchText.startsWith('@') || matchText.startsWith('#');
              const charBeforeMatch = i > 0 ? text[i - 1] : '';
              const isPrefixedWithSpecialChar = charBeforeMatch === '@' || charBeforeMatch === '#';

              // Prioritize terms over entities
              const matchesTerms = textMatchesAnyTerm(matchText);

              if (startsWithSpecialChar || isPrefixedWithSpecialChar || matchesTerms) {
                // Keep as plain text
                result += matchText;
              } else {
                // Add any text before the entity
                if (result.length > 0) {
                  newNodes.push($createTextNode(result));
                }

                const matchInfo = exactMatches.find(
                  ({ match }) => match.toLowerCase() === matchText.toLowerCase()
                );

                // Create entity node
                const entityNode = new MatchingEntityMentionNode(
                  matchText,
                  matchInfo?.suggestions || [],
                  sources
                );

                newNodes.push(entityNode);
                result = '';
              }
              i = matchEnd;
            } else {
              result += text[i];
              i += 1;
            }
          }

          // Add any remaining text
          if (result.length > 0) {
            newNodes.push($createTextNode(result));
          }

          return newNodes;
        });

        // Replace paragraph children with processed nodes
        paragraphNode.getChildren().forEach((child) => child.remove());
        paragraphNode.append(...newChildren);

        // Add text nodes around entities for proper cursor positioning
        const finalChildren = paragraphNode.getChildren();
        const hasEntityNodes = finalChildren.some((child) => child.getType() === 'entity');

        if (hasEntityNodes) {
          ensureTextNodesAroundSpecialNodes(paragraphNode, 'entity');
        }

        // Add trailing space after last entity if needed for cursor placement
        const lastChild = finalChildren[finalChildren.length - 1];

        if (lastChild && lastChild.getType() === 'entity') {
          const hasTermNodes = finalChildren.some((child) => child.getType() === 'term');
          const wasEntityCreated = newChildren.some(
            (childNode) => childNode.getType() === 'entity'
          );

          // Only add space if we created entities and there are no term nodes
          if (wasEntityCreated && !hasTermNodes) {
            paragraphNode.append($createTextNode(' '));
          }
        }
      });

      // Restore cursor position after transformation
      if (restoreCursor && savedParagraphKey) {
        const paragraph = $getNodeByKey(savedParagraphKey);
        if (paragraph) {
          const anchorPoint = findPositionInParagraph(paragraph, savedTextOffset);
          if (anchorPoint) {
            const newSelection = $createRangeSelection();
            newSelection.anchor.set(anchorPoint.node.getKey(), anchorPoint.offset, 'text');
            newSelection.focus.set(anchorPoint.node.getKey(), anchorPoint.offset, 'text');
            $setSelection(newSelection);
          } else if (!anchorPoint && paragraph) {
            paragraph.selectEnd();
          }
        }
      }
    },
    { discrete: true }
  );
}

const MatchingEntityHighlightPlugin = ({ entities = [], onSelectEntity, sources, terms = [] }) => {
  const [editor] = useLexicalComposerContext();
  const entitiesRef = useRef(entities);
  const termsRef = useRef(terms);
  const transformationTimeoutRef = useRef(null);
  const lastContentHashRef = useRef('');
  const transformationCountRef = useRef(0);

  // Create content hash for transformation loop detection
  const getContentHash = () => {
    let hash = '';
    editor.getEditorState().read(() => {
      const root = $getRoot();
      const paragraphs = root.getChildren().filter((node) => node.getType() === 'paragraph');

      paragraphs.forEach((paragraph) => {
        const children = paragraph.getChildren();
        hash += children.map((child) => `${child.getType()}:${child.getTextContent()}`).join('|');
      });
    });
    return hash;
  };

  useEffect(() => {
    entitiesRef.current = entities;
  }, [entities]);

  useEffect(() => {
    termsRef.current = terms;
  }, [terms]);

  useEffect(() => {
    if (onSelectEntity) {
      setEntitySelectCallback(onSelectEntity);
    }

    return () => {
      setEntitySelectCallback(null);
    };
  }, [onSelectEntity]);

  // Debounced transformation with loop prevention and timing coordination
  function performTransformation(
    entitiesToUse,
    delay = 0,
    restoreCursor = true,
    textOffset = null,
    paragraphKey = null
  ) {
    // Ensure EntityHighlightPlugin runs after TermHighlightPlugin for proper priority
    const hasTerms = termsRef.current && termsRef.current.length > 0;
    const adjustedDelay = hasTerms ? Math.max(delay + 100, 200) : delay;

    if (transformationTimeoutRef.current) {
      clearTimeout(transformationTimeoutRef.current);
    }

    transformationTimeoutRef.current = setTimeout(() => {
      // Prevent infinite transformation loops
      const currentHash = getContentHash();

      if (currentHash === lastContentHashRef.current) {
        transformationTimeoutRef.current = null;
        transformationCountRef.current = 0;
        return;
      }

      // Reset counter on significant content changes
      if (Math.abs(currentHash.length - lastContentHashRef.current.length) > 10) {
        transformationCountRef.current = 0;
      }

      transformationCountRef.current += 1;

      // Limit rapid successive transformations
      if (transformationCountRef.current > 3) {
        transformationTimeoutRef.current = null;
        transformationCountRef.current = 0;
        return;
      }

      transformTextNodesToEntityMentions(
        editor,
        entitiesToUse,
        sources,
        restoreCursor,
        textOffset,
        paragraphKey,
        termsRef.current
      );

      lastContentHashRef.current = getContentHash();
      transformationTimeoutRef.current = null;

      // Reset count after successful transformation
      setTimeout(() => {
        transformationCountRef.current = 0;
      }, 1000);
    }, adjustedDelay);
  }

  // Trigger transformation when entities change
  useEffect(() => {
    performTransformation(entities, 100);
  }, [editor, entities, sources]);

  useEffect(() => {
    const registrations = [
      // Handle typing with appropriate debouncing
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          if (event.metaKey || event.ctrlKey) return false;

          // Transform after user stops typing
          if (/^[a-zA-Z0-9\s]$/.test(event.key)) {
            performTransformation(entitiesRef.current, 300, true);
          } else if (event.key === 'Backspace') {
            // Handle backspace with immediate cursor position capture
            setTimeout(() => {
              editor.update(() => {
                const selection = $getSelection();
                if (selection && $isRangeSelection(selection)) {
                  const anchorNode = selection.anchor.getNode();
                  let paragraph = anchorNode;

                  // Find containing paragraph
                  while (paragraph && paragraph.getType() !== 'paragraph') {
                    paragraph = paragraph.getParent();
                  }

                  if (paragraph) {
                    const currentOffset = getTextOffsetInParagraph(
                      paragraph,
                      anchorNode,
                      selection.anchor.offset
                    );

                    // Transform with preserved cursor position
                    performTransformation(
                      entitiesRef.current,
                      0,
                      true,
                      currentOffset,
                      paragraph.getKey()
                    );
                  }
                }
              });
            }, 0);
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),

      // Handle paste events
      editor.registerCommand(
        PASTE_COMMAND,
        () => {
          performTransformation(entitiesRef.current, 100);
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    ];

    // Register entity click handler if callback provided
    if (onSelectEntity) {
      registrations.push(
        editor.registerCommand(
          CLICK_COMMAND,
          (event) => {
            const targetNode = event.target?.closest('[data-entity-mention="true"]');
            if (targetNode) {
              const nodeKey = targetNode.getAttribute('data-lexical-node-key');
              if (nodeKey) {
                editor.update(() => {
                  const node = editor.getElementByKey(nodeKey);
                  if (node && node.__type === 'entity') {
                    const text = node.getTextContent();
                    const suggestions = node.__suggestions || [];
                    onSelectEntity(text, suggestions[0] || null);
                  }
                });
              }
            }
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      );
    }

    return mergeRegister(...registrations);
  }, [editor, onSelectEntity, sources]);

  return null;
};

MatchingEntityHighlightPlugin.propTypes = {
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      entity: PropTypes.string.isRequired,
      suggestions: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          column: PropTypes.string,
          schema: PropTypes.string,
          table: PropTypes.string,
          score: PropTypes.number
        })
      )
    })
  ),
  sources: PropTypes.arrayOf(sourceShape),
  onSelectEntity: PropTypes.func,
  terms: PropTypes.arrayOf(PropTypes.string)
};

export { MatchingEntityHighlightPlugin };
